import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';
import 'owl.carousel';
import { BadgeModalComponent } from '../../shared/badge-modal/badge-modal.component';
import { OeAppService } from '../../shared/oe-app.service';
import { TranslationService } from '../../shared/translation.service';
import { GamificationBadgeCategoryType } from '../../shared/types/gamification-badge-category-type';
import { GamificationBadgeDto } from '../../shared/types/GamificationBadgeDto';
import { GamificationPersonBadgeDto } from '../../shared/types/GamificationPersonBadgeDto';
import { GamificationSettingsDto } from '../../shared/types/GamificationSettingsDto';

declare var $: any;

interface BadgeInfo {
  width: number;

  isSlider: boolean;
}

@Component({
  selector: 'lp2-badges-section',
  templateUrl: './badges-section.component.html',
  styleUrls: ['./badges-section.component.scss'],
})
export class BadgesSectionComponent implements AfterViewInit {
  @Input()
  set personBadges(data: GamificationPersonBadgeDto[]) {
    if (data) {
      this.weeklyBadges = data.filter(
        (e: GamificationPersonBadgeDto) =>
          e.activityBadge.category === GamificationBadgeCategoryType.WEEKLY
      );
      this.specialBadges = data.filter(
        (e: GamificationPersonBadgeDto) =>
          e.activityBadge.category === GamificationBadgeCategoryType.SPECIAL
      );
      this.performanceBadges = data.filter(
        (e: GamificationPersonBadgeDto) =>
          e.activityBadge.category === GamificationBadgeCategoryType.PERFORMANCE
      );
    }

    this.precalculateSliders();
  }

  @Input()
  set gamificationSettings(data: GamificationSettingsDto) {
    if (data) {
      this.topQualify = data.promotionZoneIndex;
    }
  }

  @ViewChild('weeklyContainerRef')
  weeklyContainerRef: ElementRef;

  @ViewChild('specialContainerRef')
  specialContainerRef: ElementRef;

  @ViewChild('performanceContainerRef')
  performanceContainerRef: ElementRef;

  modalRef: NgbModalRef;

  topQualify = 10;

  weeklyBadges: GamificationPersonBadgeDto[];

  specialBadges: GamificationPersonBadgeDto[];

  performanceBadges: GamificationPersonBadgeDto[];

  gamificationS3Url = `//${this.oeApp.s3BucketHost()}/gamification`;

  arrPlaceholders = Array(6).fill(1);

  tmrResize;

  // On low resolution each badge has 160px and the margin of container is 16px (left & right) so we add 32px
  // Start at 400 because of an issue of the owl carousel which ignores the values if resolution is too low
  sliderSettings = {
    nav: true,
    items: 1,
    responsive: {
      400: {
        items: 2,
        slideBy: 1,
      },
      512: {
        items: 3,
        slideBy: 1,
      },
      672: {
        items: 4,
        slideBy: 1,
      },
      832: {
        items: 5,
        slideBy: 1,
      },
    },
  };

  weeklySliderSettings;

  specialSliderSettings;

  performanceSliderSettings;

  weeklyBadgeInfo: BadgeInfo = {
    width: 160,
    isSlider: false,
  };

  specialBadgeInfo: BadgeInfo = {
    width: 182,
    isSlider: false,
  };

  performanceBadgeInfo: BadgeInfo = {
    width: 140,
    isSlider: false,
  };

  carouselSettings = {};

  currentLanguage: string;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.precalculateSliders();
  }

  constructor(
    private oeApp: OeAppService,
    private modalService: NgbModal,
    private translationService: TranslationService
  ) {
    // Weekly badges container is full row at any resolution
    this.weeklySliderSettings = _.cloneDeep(this.sliderSettings);
    this.weeklySliderSettings.responsive[992] = {
      items: 6,
      slideBy: 1,
    };
    this.weeklySliderSettings.responsive[1200] = {
      items: 7,
      slideBy: 1,
    };

    // Special badges container is fixed width at > 990 resolution
    this.specialSliderSettings = _.cloneDeep(this.sliderSettings);
    this.specialSliderSettings.responsive[991] = {
      items: 2,
      slideBy: 1,
    };

    // Performance badges container is the remaining space after the special badges container at > 990 resolution
    this.performanceSliderSettings = _.cloneDeep(this.sliderSettings);
    this.performanceSliderSettings.responsive[991] = {
      items: 3,
      slideBy: 1,
    };
    this.performanceSliderSettings.responsive[1200] = {
      items: 5,
      slideBy: 1,
    };
  }

  ngAfterViewInit() {
    this.currentLanguage = this.translationService.getCurrentLanguage();

    this.precalculateSliders();
  }

  selectBadge(badge: GamificationBadgeDto) {
    this.modalRef = this.modalService.open(BadgeModalComponent, {
      size: 'lg',
    });
    this.modalRef.componentInstance.hasTopDescription = false;
    this.modalRef.componentInstance.badge = badge;
    this.modalRef.componentInstance.topQualify = this.topQualify;
  }

  precalculateSliders() {
    if (this.tmrResize) {
      clearTimeout(this.tmrResize);
    }

    this.tmrResize = setTimeout(() => this.calculateSliders(), 200);
  }

  calculateSliders() {
    // Calculate badges rendered width
    if (document.documentElement.clientWidth > 990) {
      this.specialBadgeInfo.width = 182;
      this.performanceBadgeInfo.width = 140;
    } else {
      this.specialBadgeInfo.width = 160;
      this.performanceBadgeInfo.width = 160;
    }

    // Check if any badges and if need to display a slider
    this.checkCarousel(
      this.weeklyContainerRef,
      this.weeklyBadges,
      this.weeklyBadgeInfo,
      this.weeklySliderSettings
    );
    this.checkCarousel(
      this.specialContainerRef,
      this.specialBadges,
      this.specialBadgeInfo,
      this.specialSliderSettings
    );
    this.checkCarousel(
      this.performanceContainerRef,
      this.performanceBadges,
      this.performanceBadgeInfo,
      this.performanceSliderSettings
    );
  }

  checkCarousel(
    containerRef: ElementRef,
    badges: GamificationPersonBadgeDto[],
    badgeInfo: BadgeInfo,
    sliderSettings
  ) {
    // Check if badges container exist
    if (containerRef) {
      if (
        containerRef.nativeElement.clientWidth <
        badges.length * badgeInfo.width
      ) {
        // Enable slider
        if (!badgeInfo.isSlider) {
          badgeInfo.isSlider = true;
          setTimeout(() => {
            // Prevent empty placeholder rendering on HTML because of the isSlider (owl carousel bug)
            $(containerRef.nativeElement).owlCarousel(sliderSettings);
          });
        }
      } else {
        // Disable slider
        if (badgeInfo.isSlider) {
          badgeInfo.isSlider = undefined;
          $(containerRef.nativeElement).owlCarousel('destroy');
          $(containerRef.nativeElement).find('.owl-item,.owl-stage').remove();
          setTimeout(() => (badgeInfo.isSlider = false), 100);
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// ./ts/app/progress/badges-section/badges-section.component.ts